// client/src/hooks/usePolling.js
import { useEffect, useRef } from 'react';
import useTabVisible from './useTabVisible';

/**
 * setInterval that stops while the page is off screen.
 *
 * Keep-alive tabs stay mounted, so a plain setInterval would keep every open tab
 * hitting the API forever — five open tabs, five times the traffic. This pauses
 * hidden tabs and does one catch-up call when the tab comes back, so what you
 * see on return is fresh rather than however stale it went.
 *
 * @param callback    polled function; always the latest render's closure
 * @param intervalMs  poll period
 * @param options.enabled    skip polling entirely (e.g. feature disabled)
 * @param options.immediate  also fire once on mount, not just on re-focus
 */
export default function usePolling(callback, intervalMs, options = {}) {
  const { enabled = true, immediate = false } = options;
  const visible = useTabVisible();

  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  // Distinguishes "first run" from "came back after being hidden".
  const wentHiddenRef = useRef(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!visible) {
      if (startedRef.current) wentHiddenRef.current = true;
      return undefined;
    }

    if (wentHiddenRef.current || (immediate && !startedRef.current)) {
      wentHiddenRef.current = false;
      callbackRef.current();
    }
    startedRef.current = true;

    const id = setInterval(() => callbackRef.current(), intervalMs);
    return () => clearInterval(id);
  }, [enabled, visible, intervalMs, immediate]);
}
